import { useCallback, useEffect, useRef, useState } from 'react';
import { DeskError } from './api';

/**
 * One GET, kept fresh.
 *
 * Deliberately small and hand-written rather than a query library. The desk
 * has nine read routes and one screen open at a time; what this actually needs
 * is "fetch it, poll it, let me refetch it after a write, and never blank the
 * screen while it reloads".
 *
 * THE LAST PART IS THE ONE THAT MATTERS. An operator reading a card while the
 * poll fires must not have the card replaced by a spinner — the previous data
 * stays on screen until the new data arrives, and a failed refresh leaves what
 * was there with the error beside it. A queue that empties itself every thirty
 * seconds is a queue you cannot read.
 */
export interface Resource<T> {
  data: T | null;
  error: DeskError | null;
  loading: boolean;   // the FIRST load only
  refreshing: boolean;
  refetch: () => Promise<void>;
}

export function useResource<T>(
  fetcher: () => Promise<T>,
  { pollMs = 0, enabled = true, deps = [] as unknown[] } = {},
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<DeskError | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshing, setRefreshing] = useState(false);

  // The fetcher is a new closure on every render; holding it in a ref keeps it
  // out of the effect's dependencies, so the poll is not torn down and rebuilt
  // sixty times a second.
  const fn = useRef(fetcher);
  fn.current = fetcher;
  const alive = useRef(true);

  const run = useCallback(async (first: boolean) => {
    if (first) setLoading(true); else setRefreshing(true);
    try {
      const next = await fn.current();
      if (!alive.current) return;
      setData(next);
      setError(null);
    } catch (e) {
      if (!alive.current) return;
      // The previous data stays. An operator mid-decision does not lose the
      // card because a poll timed out.
      setError(e instanceof DeskError ? e : new DeskError(String(e), 0));
    } finally {
      if (alive.current) { setLoading(false); setRefreshing(false); }
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!enabled) { setLoading(false); return () => { alive.current = false; }; }

    run(true);
    let timer: ReturnType<typeof setInterval> | null = null;
    if (pollMs > 0) {
      timer = setInterval(() => {
        // Polling a tab nobody is looking at spends D1 reads to refresh a
        // screen that is not on screen.
        if (document.visibilityState === 'visible') run(false);
      }, pollMs);
    }
    const onVisible = () => { if (document.visibilityState === 'visible') run(false); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive.current = false;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pollMs, ...deps]);

  return { data, error, loading, refreshing, refetch: () => run(false) };
}

/** A clock that ticks, for the veto countdowns and the card ages. */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}
